import React, { useCallback, useMemo } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { useAppSelector } from 'store/hooks';
import { rem, SettingsItemCard } from 'design-system';
import { IconBank } from 'design-system/icons';
import { IconBankCard } from 'design-system/icons/IconBankCard';
import { IconEnergyBolt } from 'design-system/icons/IconEnergyBolt';
import { IconMoneyCheckPenSolid } from 'design-system/icons/IconMoneyCheckPenSolid';
import IconMortgage from 'design-system/icons/IconMortgage';
import { IconWifi } from 'design-system/icons/IconWifi';
import useMonevoEntry from 'features/borrow/monevo/hooks/useMonevoEntry';
import useEnergyCompare from 'features/feed/components/save/useEnergyCompare';
import { selectFeatureFlag } from 'reducers/selectors';
import Links from 'utils/links';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
const PLACEHOLDER_KEY = '__placeholder__';
const LoanItemsList = ({ excludeKeys }) => {
    const showMortgages = useAppSelector((store) => selectFeatureFlag(store, 'mortgages_habito').value);
    const mortgagesUrl = useAppSelector((store) => selectFeatureFlag(store, 'mortgages_habito').extra?.url);
    const openMonevo = useMonevoEntry();
    const navigation = useMainStackNavigation();
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigate(routeWithParams);
    }, []);
    const energyCompare = useEnergyCompare(onCompare);
    const hasEnergy = useAppSelector((store) => selectFeatureFlag(store, 'energy_switch_two').value);
    const hasCurrentAccountSwitchOffers = useAppSelector((store) => {
        const { value, extra } = selectFeatureFlag(store, 'current_account_switch_offers');
        return value && extra?.offers?.length;
    });
    const onPressCurrentAccounts = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://borrow', 'Compare accounts');
            return;
        }
        navigation.navigate('CurrentAccountSwitchOffers');
    }, []);
    const onPressBroadband = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://borrow', 'View broadband deals');
            return;
        }
        energyCompare('Broadband');
    }, [energyCompare]);
    const onPressEnergy = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://borrow', 'View energy deals');
            return;
        }
        energyCompare('Energy');
    }, [energyCompare]);
    const items = useMemo(() => {
        const list = [
            {
                key: 'cards',
                icon: <IconBankCard gradientKey="darkPink"/>,
                title: 'Credit cards',
                description: 'Find the right card for you',
                onPress: isWeb
                    ? () => Links.openKnownLink('creditCards')
                    : () => openMonevo('CREDIT_CARD'),
            },
            {
                key: 'loans',
                icon: <IconMoneyCheckPenSolid gradientKey="apricot"/>,
                title: 'Loans',
                description: 'Browse short and long term loans',
                onPress: isWeb
                    ? () => Links.openKnownLink('loans')
                    : () => openMonevo('LOAN'),
            },
        ];
        if (showMortgages) {
            list.push({
                key: 'mortgages',
                icon: <IconMortgage gradientKey="orange"/>,
                title: 'Mortgages',
                description: 'Find your perfect mortgage',
                onPress: () => mortgagesUrl
                    ? Links.openLink(mortgagesUrl)
                    : Links.openKnownLink('mortgagesHabito'),
            });
        }
        if (hasCurrentAccountSwitchOffers) {
            list.push({
                key: 'accounts',
                icon: <IconBank gradientKey="lightPurple"/>,
                title: 'Accounts',
                description: 'Compare current accounts',
                onPress: onPressCurrentAccounts,
            });
        }
        list.push({
            key: 'broadband',
            icon: <IconWifi gradientKey="darkPurple"/>,
            title: 'Broadband',
            description: 'Save on broadband',
            onPress: onPressBroadband,
        });
        if (hasEnergy) {
            list.push({
                key: 'energy',
                icon: <IconEnergyBolt gradientKey="yellow"/>,
                title: 'Energy',
                description: 'Save on Energy',
                onPress: onPressEnergy,
            });
        }
        return list.filter((item) => !excludeKeys?.includes(item.key));
    }, [
        excludeKeys,
        hasCurrentAccountSwitchOffers,
        hasEnergy,
        openMonevo,
        mortgagesUrl,
        onPressBroadband,
        onPressCurrentAccounts,
        onPressEnergy,
        showMortgages,
    ]);
    const data = useMemo(() => {
        if (items.length % 2 === 1) {
            return [
                ...items,
                {
                    key: PLACEHOLDER_KEY,
                    icon: null,
                    title: '',
                    description: '',
                    onPress: () => { },
                },
            ];
        }
        return items;
    }, [items]);
    const renderItem = useCallback(({ item }) => {
        if (item.key === PLACEHOLDER_KEY) {
            return <View style={styles.placeholder}/>;
        }
        return (<SettingsItemCard icon={item.icon} title={item.title} description={item.description} onPress={item.onPress} compactText descriptionMaxLines={2}/>);
    }, []);
    const keyExtractor = useCallback((item) => item.key, []);
    return (<FlatList data={data} numColumns={2} scrollEnabled={false} keyExtractor={keyExtractor} renderItem={renderItem} columnWrapperStyle={styles.row} contentContainerStyle={styles.container}/>);
};
const styles = StyleSheet.create({
    container: {
        paddingHorizontal: rem(16),
        gap: rem(16),
    },
    row: {
        gap: rem(16),
    },
    placeholder: {
        flex: 1,
        padding: rem(16),
    },
});
export default LoanItemsList;
